import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { AdminHeader, Drawer } from '@/features/admin/ui';
import { useRequireRole } from '@/lib/auth/useRequireRole';
import { adminQueries, auditLog, type MastheadEntry } from '@/lib/queries/admin';
import { supabase } from '@/lib/supabase';

export const Route = createFileRoute('/admin/masthead')({
  component: MastheadPage,
});

// Mirror the profile-avatar upload guard (SettingsPage).
const MASTHEAD_ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const MASTHEAD_MAX_BYTES = 2 * 1024 * 1024;

const GROUPS = [
  { key: 'publisher', label: 'Publishers' },
  { key: 'editor', label: 'Editors' },
  { key: 'contributor', label: 'Contributors' },
] as const;

type ExternalLink = { label: string; url: string };

function MastheadPage() {
  useRequireRole(['admin']); // admin-only; moderators 403 on direct nav (Spec 21)
  const qc = useQueryClient();
  const list = useQuery(adminQueries.masthead());
  const rows = list.data ?? [];
  const [editing, setEditing] = useState<Partial<MastheadEntry> | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'masthead'] });

  const reorder = useMutation({
    mutationFn: async ({ row, dir }: { row: MastheadEntry; dir: -1 | 1 }) => {
      const peers = rows
        .filter((r) => r.role_group === row.role_group)
        .sort((a, b) => a.display_order - b.display_order);
      const idx = peers.findIndex((p) => p.id === row.id);
      const swap = peers[idx + dir];
      if (!swap) return;
      await supabase
        .from('masthead_entries')
        .update({ display_order: swap.display_order })
        .eq('id', row.id);
      await supabase
        .from('masthead_entries')
        .update({ display_order: row.display_order })
        .eq('id', swap.id);
    },
    onSuccess: invalidate,
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Reorder failed.'),
  });

  const remove = useMutation({
    mutationFn: async (row: MastheadEntry) => {
      const { error } = await supabase.from('masthead_entries').delete().eq('id', row.id);
      if (error) throw error;
      await auditLog('masthead.deleted', 'masthead_entries', row.id, {
        display_name: row.display_name,
      });
    },
    onSuccess: () => {
      toast.success('Masthead entry deleted.');
      invalidate();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Delete failed.'),
  });

  return (
    <div className="mx-auto max-w-4xl">
      <AdminHeader kicker="Admin · Publication" title="Masthead" />

      {GROUPS.map((g) => {
        const groupRows = rows
          .filter((r) => r.role_group === g.key)
          .sort((a, b) => a.display_order - b.display_order);
        return (
          <section key={g.key} className="mt-10">
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-xl text-ink">{g.label}</h2>
              <button
                type="button"
                onClick={() =>
                  setEditing({
                    role_group: g.key,
                    is_published: false,
                    display_order: groupRows.length,
                  })
                }
                className="rounded-md border border-ink/20 px-3 py-1.5 font-sans text-xs font-medium uppercase tracking-[0.12em] text-ink hover:border-oxblood hover:text-oxblood"
              >
                Add entry
              </button>
            </div>
            {groupRows.length === 0 ? (
              <p className="mt-3 font-sans text-sm text-ink/50">No entries.</p>
            ) : (
              <ul className="mt-3 divide-y divide-ink/10">
                {groupRows.map((r, i) => (
                  <li key={r.id} className="flex items-center gap-3 py-3">
                    <span className="flex flex-col">
                      <button
                        type="button"
                        disabled={i === 0}
                        onClick={() => reorder.mutate({ row: r, dir: -1 })}
                        className="text-ink/40 hover:text-oxblood disabled:opacity-20"
                        aria-label="Move up"
                      >
                        <ChevronUp className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        disabled={i === groupRows.length - 1}
                        onClick={() => reorder.mutate({ row: r, dir: 1 })}
                        className="text-ink/40 hover:text-oxblood disabled:opacity-20"
                        aria-label="Move down"
                      >
                        <ChevronDown className="h-4 w-4" />
                      </button>
                    </span>
                    <button
                      type="button"
                      onClick={() => setEditing(r)}
                      className="flex-1 text-left"
                    >
                      <span className="font-serif text-base text-ink">{r.display_name}</span>
                      {r.title && (
                        <span className="ml-2 font-sans text-xs text-ink/50">{r.title}</span>
                      )}
                    </button>
                    <span
                      className={`rounded-sm px-2 py-0.5 font-sans text-[0.7rem] uppercase tracking-[0.12em] ${
                        r.is_published
                          ? 'bg-emerald-600/10 text-emerald-700'
                          : 'bg-ink/10 text-ink/50'
                      }`}
                    >
                      {r.is_published ? 'Published' : 'Draft'}
                    </span>
                    <button
                      type="button"
                      disabled={remove.isPending}
                      onClick={() => {
                        if (
                          window.confirm(
                            `Remove ${r.display_name} from the masthead? This can't be undone.`,
                          )
                        ) {
                          remove.mutate(r);
                        }
                      }}
                      className="font-sans text-xs text-oxblood hover:underline disabled:opacity-60"
                    >
                      Delete
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        );
      })}

      <Drawer
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        title={editing?.id ? 'Edit entry' : 'New entry'}
      >
        {editing && (
          <EntryForm
            entry={editing}
            onDone={() => {
              setEditing(null);
              invalidate();
            }}
          />
        )}
      </Drawer>
    </div>
  );
}

function EntryForm({ entry, onDone }: { entry: Partial<MastheadEntry>; onDone: () => void }) {
  const [form, setForm] = useState({
    display_name: entry.display_name ?? '',
    title: entry.title ?? '',
    bio: entry.bio ?? '',
    photo_url: entry.photo_url ?? '',
    is_published: entry.is_published ?? false,
    focus_areas: (entry.focus_areas ?? []).join(', '),
  });
  const [links, setLinks] = useState<ExternalLink[]>(
    Array.isArray(entry.external_links) ? (entry.external_links as unknown as ExternalLink[]) : [],
  );
  const [uploading, setUploading] = useState(false);

  const uploadPhoto = async (file: File) => {
    // Guard mirroring the profile-avatar upload (confirm 3).
    if (!MASTHEAD_ALLOWED_MIME.includes(file.type)) {
      toast.error('Use a JPEG, PNG, WEBP, or GIF.');
      return;
    }
    if (file.size > MASTHEAD_MAX_BYTES) {
      toast.error('Image must be under 2 MB.');
      return;
    }
    setUploading(true);
    try {
      const ext = file.name.split('.').pop() ?? 'jpg';
      const path = `${crypto.randomUUID()}.${ext}`;
      const { error } = await supabase.storage
        .from('masthead-photos')
        .upload(path, file, { upsert: true, contentType: file.type });
      if (error) throw error;
      const { data } = supabase.storage.from('masthead-photos').getPublicUrl(path);
      setForm((f) => ({ ...f, photo_url: data.publicUrl }));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const save = useMutation({
    mutationFn: async () => {
      if (!form.display_name.trim()) throw new Error('Display name is required.');
      const payload = {
        role_group: entry.role_group!,
        display_name: form.display_name.trim(),
        title: form.title.trim() || null,
        bio: form.bio.trim() || null,
        photo_url: form.photo_url || null,
        is_published: form.is_published,
        display_order: entry.display_order ?? 0,
        focus_areas: form.focus_areas
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
        external_links: links.filter((l) => l.label && l.url).slice(0, 5),
      };
      if (entry.id) {
        const { error } = await supabase
          .from('masthead_entries')
          .update(payload)
          .eq('id', entry.id);
        if (error) throw error;
        await auditLog('masthead.updated', 'masthead_entries', entry.id, {
          published: payload.is_published,
        });
      } else {
        const { data, error } = await supabase
          .from('masthead_entries')
          .insert(payload)
          .select('id')
          .single();
        if (error) throw error;
        await auditLog('masthead.created', 'masthead_entries', data.id, {
          published: payload.is_published,
        });
      }
    },
    onSuccess: () => {
      toast.success('Masthead entry saved.');
      onDone();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Save failed.'),
  });

  return (
    <div className="space-y-4">
      <Input
        label="Display name"
        value={form.display_name}
        onChange={(v) => setForm((f) => ({ ...f, display_name: v }))}
      />
      <Input
        label="Title"
        value={form.title}
        onChange={(v) => setForm((f) => ({ ...f, title: v }))}
      />
      <div>
        <Label>Bio</Label>
        <textarea
          rows={4}
          value={form.bio}
          onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))}
          className="w-full rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none"
        />
      </div>
      <Input
        label="Focus areas (comma-separated)"
        value={form.focus_areas}
        onChange={(v) => setForm((f) => ({ ...f, focus_areas: v }))}
      />

      <div>
        <Label>Photo</Label>
        {form.photo_url && (
          <img src={form.photo_url} alt="" className="mb-2 h-20 w-20 rounded-full object-cover" />
        )}
        <input
          type="file"
          accept={MASTHEAD_ALLOWED_MIME.join(',')}
          disabled={uploading}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void uploadPhoto(file);
          }}
          className="block font-sans text-sm text-ink/70"
        />
      </div>

      <div>
        <Label>External links (max 5)</Label>
        {links.map((l, i) => (
          <div key={i} className="mb-2 flex gap-2">
            <input
              placeholder="Label"
              value={l.label}
              onChange={(e) =>
                setLinks((ls) => ls.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))
              }
              className="w-1/3 rounded-md border border-ink/20 bg-parchment px-2 py-1.5 text-sm text-ink focus:border-oxblood focus:outline-none"
            />
            <input
              placeholder="https://"
              value={l.url}
              onChange={(e) =>
                setLinks((ls) => ls.map((x, j) => (j === i ? { ...x, url: e.target.value } : x)))
              }
              className="flex-1 rounded-md border border-ink/20 bg-parchment px-2 py-1.5 text-sm text-ink focus:border-oxblood focus:outline-none"
            />
            <button
              type="button"
              onClick={() => setLinks((ls) => ls.filter((_, j) => j !== i))}
              className="font-sans text-xs text-oxblood"
            >
              Remove
            </button>
          </div>
        ))}
        {links.length < 5 && (
          <button
            type="button"
            onClick={() => setLinks((ls) => [...ls, { label: '', url: '' }])}
            className="font-sans text-xs text-oxblood underline"
          >
            Add link
          </button>
        )}
      </div>

      <label className="flex items-center gap-2 font-sans text-sm text-ink/80">
        <input
          type="checkbox"
          checked={form.is_published}
          onChange={(e) => setForm((f) => ({ ...f, is_published: e.target.checked }))}
          className="accent-oxblood"
        />
        Published (visible on the public masthead)
      </label>

      <button
        type="button"
        disabled={save.isPending}
        onClick={() => save.mutate()}
        className="rounded-md bg-ink px-4 py-2 font-sans text-sm font-medium text-parchment hover:bg-oxblood disabled:opacity-60"
      >
        Save entry
      </button>
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <label className="mb-1 block font-sans text-xs uppercase tracking-[0.12em] text-ink/60">
      {children}
    </label>
  );
}

function Input({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <Label>{label}</Label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none"
      />
    </div>
  );
}
